import { useState } from "react";
import { Alert, Box, Button, Snackbar } from "@mui/material";
import Search from "./rsvp_components/Search";
import Matches from "./rsvp_components/Matches";
import Form from "./rsvp_components/Form";
import { REACT_APP_API_URL } from "../constants/dev";

export default function RSVP() {
  const [matchedRSVPs, setMatchedRSVPs] = useState([]);
  const [selectedRSVP, setSelectedRSVP] = useState({});

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertCode, setAlertCode] = useState(0);
  const [alertMessage, setAlertMessage] = useState("");

  const confirmationValidation = () => {
    const { rsvp } = selectedRSVP;
    const guestIDs = Object.keys(rsvp);
    let hasNoNames = false;
    for (const guestID of guestIDs) {
      const guest = rsvp[guestID];
      const { name, coming, meal } = guest;
      if (name.trim() === "") {
        hasNoNames = true;
      } else {
        if (name.trim().split(" ").length === 1) {
          return 1;
        }
        if (coming === "") {
          return 2;
        }
        if (coming === "coming" && meal === "") {
          return 3;
        }
      }
    }
    if (hasNoNames) return 4;
    return 0;
  };

  const handleMatches = (matchedRSVPs) => {
    setSelectedRSVP({});
    setMatchedRSVPs(matchedRSVPs);
  };

  const handleSelect = (selectedRSVP) => {
    setSelectedRSVP(selectedRSVP);
  };

  const handleFormChange = (id, newValues) => {
    if (selectedRSVP.rsvp && Object.keys(selectedRSVP.rsvp).includes(id)) {
      setSelectedRSVP((prev) => {
        if (prev.id && prev.rsvp) {
          return {
            id: prev.id,
            rsvp: {
              ...prev.rsvp,
              [id]: newValues,
            },
          };
        }
      });
    }
  };

  const handleConfirm = () => {
    if (selectedRSVP.rsvp) {
      const validationCode = confirmationValidation();
      if (
        validationCode === 0 ||
        (validationCode === 4 &&
          window.confirm(
            "One or more of your +1 guests have been left empty, and will be considered to be not attending. Is this ok?",
          ))
      ) {
        setAlertOpen(false);
        setAlertCode(0);
        setAlertMessage("");
        fetch(`${REACT_APP_API_URL}/rsvp/confirm`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id: selectedRSVP.id,
            rsvp: selectedRSVP.rsvp,
          }),
        }).then((response) => {
          if (response.status === 200) {
            setAlertCode(0);
            setAlertMessage("Confirmation successful! Thank you!");
            setAlertOpen(true);
          } else if (response.status === 500) {
            setAlertCode(-1);
            setAlertMessage("Error! Please refresh and try again.");
            setAlertOpen(true);
          }
        });
      } else if (validationCode === 1) {
        setAlertCode(1);
        setAlertMessage(
          "Please fill in the first and last name of all guests in your party.",
        );
        setAlertOpen(true);
      } else if (validationCode === 2) {
        setAlertCode(2);
        setAlertMessage(
          "Please indicate whether each guest will be attending or if they are unable to attend.",
        );
        setAlertOpen(true);
      } else if (validationCode === 3) {
        setAlertCode(3);
        setAlertMessage(
          "Please choose a meal for all attending guests in your party.",
        );
        setAlertOpen(true);
      }
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Search onMatch={handleMatches} />
      {Object.keys(selectedRSVP).length === 0 && matchedRSVPs.length > 0 && (
        <Matches matchedRSVPs={matchedRSVPs} onSelect={handleSelect} />
      )}
      {selectedRSVP.rsvp &&
        Object.keys(selectedRSVP.rsvp).length > 0 &&
        Object.keys(selectedRSVP.rsvp).map((rsvpID) => {
          const rsvp = selectedRSVP.rsvp[rsvpID];
          const name = rsvp.name;
          const splitNames = name
            .split(" ")
            .filter((n, i) => i === 0 || n !== "");
          const firstName = splitNames.length > 0 ? splitNames[0] : "";
          const lastName = splitNames.length > 1 ? splitNames[1] : "";
          const coming = rsvp.coming;
          const meal = rsvp.meal;
          const dietaryRestrictions = rsvp.dietary_restrictions;
          return (
            <Form
              id={rsvpID}
              firstName={firstName}
              lastName={lastName}
              coming={coming}
              meal={meal}
              dietaryRestrictions={dietaryRestrictions}
              onChange={handleFormChange}
            />
          );
        })}
      {selectedRSVP.rsvp && Object.keys(selectedRSVP.rsvp).length > 0 && (
        <Button
          variant="outlined"
          onClick={handleConfirm}
          sx={{
            margin: "1rem",
            fontSize: "1.5rem",
            padding: "0 2rem",
            backgroundColor: "rgb(80, 97, 117)",
            color: "rgb(255, 255, 237)",
            "&:hover": {
              backgroundColor: "rgb(64, 80, 97)", // slightly darker for hover
            },
          }}
        >
          Confirm
        </Button>
      )}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={alertOpen}
        autoHideDuration={10000}
        onClose={() => setAlertOpen(false)}
      >
        <Alert
          onClose={() => setAlertOpen(false)}
          severity={
            alertCode === 0 ? "success" : alertCode === -1 ? "error" : "warning"
          }
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
